import { useState } from "react";
import { Label } from "@groot/ui/label";
import { RequestStatusBadge } from "@/components/status-badges";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@groot/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@groot/ui/dialog";
import { toast } from "sonner";
import {
  usePhotoRequests,
  useCreatePhotoRequest,
  useUpdatePhotoRequest,
  useDeletePhotoRequest,
} from "@/pages/photo-request/hooks/usePhotoRequests";
import { useOrders } from "@/pages/order/hooks/useOrders";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Input } from "@groot/ui/input";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { SkeletonTable } from "@groot/ui/loading-skeleton";
import { EmptyState } from "@groot/ui/empty-state";
import { RowActions, RowActionItem, RowActionSeparator } from "@/components/row-actions";
import { Search, Camera, Plus, Trash2, Eye, Copy, CheckCircle } from "lucide-react";

const photoRequestSchema = z.object({
  orderId: z.number().int().positive("Order is required"),
  customerId: z.number().int().positive("Customer is required"),
});

type PhotoRequestFormValues = z.infer<typeof photoRequestSchema>;

export function PhotoRequests() {
  const { data: photoRequests, isLoading } = usePhotoRequests();
  const { data: orders } = useOrders();
  const createPhotoRequest = useCreatePhotoRequest();
  const updatePhotoRequest = useUpdatePhotoRequest();
  const deletePhotoRequest = useDeletePhotoRequest();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const form = useForm<PhotoRequestFormValues>({
    resolver: zodResolver(photoRequestSchema),
    defaultValues: {
      orderId: 0,
      customerId: 0,
    },
  });

  const filteredRequests = photoRequests?.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.order?.orderNumber.toLowerCase().includes(q) || r.customer?.name.toLowerCase().includes(q)
    );
  });

  const onSubmit = async (values: PhotoRequestFormValues) => {
    try {
      await createPhotoRequest.mutateAsync(values);
      toast.success("Success", { description: "Photo request created" });
      form.reset();
      setOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to create photo request" });
    }
  };

  const completeRequest = async (id: number) => {
    try {
      await updatePhotoRequest.mutateAsync({ id, data: { status: "completed" } });
      toast.success("Success", { description: "Photo request completed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to complete photo request" });
    }
  };

  const removeRequest = async (id: number) => {
    try {
      await deletePhotoRequest.mutateAsync(id);
      toast.success("Deleted", { description: "Photo request removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete photo request" });
    }
  };

  const copyPublicLink = (link: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/public/photo-requests/${link}`);
    toast.success("Copied", { description: "Public link copied to clipboard" });
  };

  const actions = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="size-4" />
          Create Request
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Photo Request</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <Label>Order</Label>
            <Select
              onValueChange={(value) => {
                const order = orders?.find((o) => o.id === parseInt(value));
                if (order) {
                  form.setValue("orderId", order.id);
                  form.setValue("customerId", order.customerId);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select order" />
              </SelectTrigger>
              <SelectContent>
                {orders?.map((order) => (
                  <SelectItem key={order.id} value={order.id.toString()}>
                    {order.orderNumber} - {order.customer?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.orderId && (
              <p className="text-sm text-destructive">{form.formState.errors.orderId.message}</p>
            )}
          </div>
          <Button className="w-full" type="submit" disabled={createPhotoRequest.isPending}>
            {createPhotoRequest.isPending ? "Creating..." : "Create Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );

  const rowActions = (request: NonNullable<typeof filteredRequests>[number]) => (
    <RowActions>
      <RowActionItem asChild>
        <Link to={`/photo-requests/${request.id}`}>
          <Eye className="size-4" />
          View
        </Link>
      </RowActionItem>
      <RowActionItem onClick={() => copyPublicLink(request.publicLink)}>
        <Copy className="size-4" />
        Copy Link
      </RowActionItem>
      {request.status === "pending" && (
        <RowActionItem
          onClick={() => completeRequest(request.id)}
          disabled={updatePhotoRequest.isPending}
        >
          <CheckCircle className="size-4" />
          Complete
        </RowActionItem>
      )}
      <RowActionSeparator />
      <RowActionItem
        destructive
        onClick={() => removeRequest(request.id)}
        disabled={deletePhotoRequest.isPending}
      >
        <Trash2 className="size-4" />
        Delete
      </RowActionItem>
    </RowActions>
  );

  return (
    <PageLayout title="Photo Requests" description="Manage photo upload requests" actions={actions}>
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search by order # or customer name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading && (
          <div className="overflow-hidden rounded-lg border">
            <SkeletonTable columns={6} rows={6} />
          </div>
        )}

        {!isLoading && filteredRequests && filteredRequests.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <ul className="md:hidden divide-y divide-border">
              {filteredRequests.map((request) => (
                <li key={request.id} className="flex items-center gap-2 py-3 pl-4 pr-2">
                  <Link to={`/photo-requests/${request.id}`} className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {request.customer?.name}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        #{request.id} · {request.order?.orderNumber}
                      </span>
                      <RequestStatusBadge status={request.status} />
                      <span>{request._count?.files ?? 0} photos</span>
                    </div>
                  </Link>
                  {rowActions(request)}
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="hidden sm:table-cell">ID</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell">Photos</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      className="cursor-pointer hover:bg-muted/50 border-border"
                    >
                      <TableCell className="text-sm text-foreground hidden sm:table-cell">
                        <Link
                          to={`/photo-requests/${request.id}`}
                          className="font-medium hover:text-primary"
                        >
                          #{request.id}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-foreground">
                        <Link to={`/orders/${request.orderId}`} className="hover:text-primary">
                          {request.order?.orderNumber}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {request.customer?.name}
                      </TableCell>
                      <TableCell>
                        <RequestStatusBadge status={request.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                        {request._count?.files ?? 0} uploaded
                      </TableCell>
                      <TableCell>{rowActions(request)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {!isLoading && filteredRequests?.length === 0 && (
          <EmptyState
            icon={Camera}
            title={search ? "No requests found" : "No photo requests yet"}
            description={
              search
                ? "Try adjusting your search terms"
                : "Create a request to collect photos from customers"
            }
            action={
              !search && (
                <Button onClick={() => setOpen(true)}>
                  <Plus className="size-4" />
                  Create Request
                </Button>
              )
            }
          />
        )}
      </div>
    </PageLayout>
  );
}
